import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as faceapi from '@vladmandic/face-api';
import axios from 'axios';
import { theme, cardStyle } from '../theme';

/**
 * FaceCheckin — webcam-based face recognition for instant patient lookup.
 *
 * Flow:
 * 1. On mount, loads face-api.js models + all stored face descriptors from DB.
 * 2. Opens webcam, continuously detects faces.
 * 3. When a face matches a stored descriptor (distance < 0.5), shows the patient.
 * 4. User clicks "Open Profile" to navigate to that patient.
 *
 * Also used during registration: after capturing a photo, the descriptor is
 * auto-saved via the /api/face/register endpoint.
 *
 * Props:
 *   API_BASE       — base API url
 *   UPLOAD_BASE    — base URL for patient photos
 *   onPatientFound — callback(patient) when a match is found and user confirms
 *   onClose        — close the checkin modal
 */
const FaceCheckin = ({ API_BASE, UPLOAD_BASE, onPatientFound, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState('Loading face recognition models...');
  const [matchedPatient, setMatchedPatient] = useState(null);
  const [matchDistance, setMatchDistance] = useState(null);
  const [faceMatcher, setFaceMatcher] = useState(null);
  const [descriptorMap, setDescriptorMap] = useState({});
  const [scanning, setScanning] = useState(false);

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
        setModelsLoaded(true);
        setStatusMsg('Models loaded. Loading patient face data...');
      } catch (e) {
        setStatusMsg('Failed to load face models. Check /public/models/ folder.');
        setLoading(false);
      }
    };
    loadModels();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  // Load all face descriptors from DB + build matcher
  useEffect(() => {
    if (!modelsLoaded) return;
    const loadDescriptors = async () => {
      try {
        const r = await axios.get(`${API_BASE}/face/all`);
        const entries = r.data;
        if (entries.length === 0) {
          setStatusMsg('No registered faces found. Register patients with photos first.');
          setLoading(false);
          return;
        }
        const labeledDescriptors = [];
        const dMap = {};
        entries.forEach(entry => {
          if (entry.patientId && entry.descriptor?.length === 128) {
            const desc = new Float32Array(entry.descriptor);
            const label = String(entry.patientId._id);
            labeledDescriptors.push(new faceapi.LabeledFaceDescriptors(label, [desc]));
            dMap[label] = entry.patientId;
          }
        });
        if (labeledDescriptors.length === 0) {
          setStatusMsg('No valid face descriptors. Register patients with photos first.');
          setLoading(false);
          return;
        }
        const matcher = new faceapi.FaceMatcher(labeledDescriptors, 0.5);
        setFaceMatcher(matcher);
        setDescriptorMap(dMap);
        setStatusMsg(`Loaded ${labeledDescriptors.length} face(s). Starting camera...`);
        startCamera();
      } catch (e) {
        setStatusMsg('Failed to load face data from server.');
        setLoading(false);
      }
    };
    loadDescriptors();
  }, [modelsLoaded]); // eslint-disable-line

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setLoading(false);
      setScanning(true);
      setStatusMsg('Looking for faces...');
    } catch (e) {
      setStatusMsg('Camera access denied. Please allow camera permission.');
      setLoading(false);
    }
  };

  // Continuous face detection loop
  const detectFace = useCallback(async () => {
    if (!videoRef.current || !faceMatcher || !scanning) return;
    const video = videoRef.current;
    if (video.readyState !== 4) {
      animRef.current = requestAnimationFrame(detectFace);
      return;
    }

    const detection = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.5 }))
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (detection) {
      const match = faceMatcher.findBestMatch(detection.descriptor);
      if (match.label !== 'unknown' && match.distance < 0.5) {
        const patient = descriptorMap[match.label];
        if (patient) {
          setMatchedPatient(patient);
          setMatchDistance(match.distance);
          setScanning(false);
          setStatusMsg('Patient identified!');
          // Draw box on canvas
          if (canvasRef.current) {
            const dims = faceapi.matchDimensions(canvasRef.current, video, true);
            const resized = faceapi.resizeResults(detection, dims);
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            faceapi.draw.drawDetections(canvasRef.current, [resized]);
          }
          return; // stop scanning
        }
      }
      // Draw detection box even if no match
      if (canvasRef.current) {
        const dims = faceapi.matchDimensions(canvasRef.current, video, true);
        const resized = faceapi.resizeResults(detection, dims);
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        faceapi.draw.drawDetections(canvasRef.current, [resized]);
      }
    }

    animRef.current = requestAnimationFrame(detectFace);
  }, [faceMatcher, descriptorMap, scanning]);

  useEffect(() => {
    if (scanning && faceMatcher) {
      animRef.current = requestAnimationFrame(detectFace);
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [scanning, faceMatcher, detectFace]);

  const handleConfirm = () => {
    if (matchedPatient && onPatientFound) {
      onPatientFound(matchedPatient);
    }
  };

  const handleRetry = () => {
    setMatchedPatient(null);
    setMatchDistance(null);
    setScanning(true);
    setStatusMsg('Looking for faces...');
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const confidence = matchDistance !== null ? Math.round((1 - matchDistance) * 100) : 0;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '20px', padding: '30px',
          width: '700px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2 style={{ margin: 0, color: theme.navy, fontSize: '1.3rem' }}>Face Check-in</h2>
            <p style={{ margin: '4px 0 0', color: theme.textMuted, fontSize: '0.85rem' }}>{statusMsg}</p>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#aaa' }}>✕</button>
        </div>

        {/* Camera view */}
        <div style={{ position: 'relative', width: '100%', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#000', marginBottom: '16px' }}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{ width: '100%', display: 'block' }}
            onPlay={() => { if (scanning) detectFace(); }}
          />
          <canvas
            ref={canvasRef}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />

          {/* Scanning indicator */}
          {scanning && (
            <div style={{
              position: 'absolute', top: '12px', left: '12px',
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 14px', backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: '20px', color: '#00ff88', fontSize: '0.82rem', fontWeight: '600',
            }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: '#00ff88',
                animation: 'pulse 1s infinite',
              }}/>
              Scanning...
            </div>
          )}

          {/* Loading overlay */}
          {loading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '1rem',
            }}>
              Loading...
            </div>
          )}
        </div>

        {/* Match result */}
        {matchedPatient && (
          <div style={{
            ...cardStyle,
            borderTop: `4px solid ${theme.success}`,
            backgroundColor: '#f0fdf4',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {matchedPatient.photo && (
                <img
                  src={/^https?:\/\//.test(matchedPatient.photo) ? matchedPatient.photo : `${UPLOAD_BASE}/${matchedPatient.photo}`}
                  alt="patient"
                  style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${theme.success}` }}
                  onError={e => e.target.style.display = 'none'}
                />
              )}
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 4px', color: theme.navy }}>{matchedPatient.name}</h3>
                <div style={{ fontSize: '0.85rem', color: theme.textMuted }}>
                  UHID: <strong>{matchedPatient.uhid}</strong>
                  {matchedPatient.phone && <> &nbsp;•&nbsp; Phone: {matchedPatient.phone}</>}
                </div>
                <div style={{ fontSize: '0.82rem', marginTop: '4px' }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.74rem', fontWeight: '700',
                    backgroundColor: confidence >= 80 ? '#d1fae5' : '#fef3c7',
                    color: confidence >= 80 ? '#065f46' : '#92400e',
                  }}>
                    {confidence}% match confidence
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button onClick={handleConfirm}
                  style={{
                    padding: '10px 22px', backgroundColor: theme.success, color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem',
                  }}>
                  Open Profile
                </button>
                <button onClick={handleRetry}
                  style={{
                    padding: '8px 18px', backgroundColor: '#6c757d', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.82rem',
                  }}>
                  Scan Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pulse animation */}
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
      </div>
    </div>
  );
};

export default FaceCheckin;
